import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { finalize, forkJoin, of } from 'rxjs';
import { CrmClient } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { Company } from 'src/app/company/company.model';
import { Customer } from 'src/app/customer/customer.model';
import { CustomerFormComponent } from 'src/app/customer/customer-form/customer-form.component';
import { CustomerService } from 'src/app/customer/customer.service';
import { Location as LocationModel } from 'src/app/location/location.model';
import { Country } from 'src/app/models/common/country.model';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { BaseMasterDataService } from 'src/app/services/base-master-data.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from 'src/app/shared/party-quick-create/party-quick-create-dialog.component';
import { PageFormSectionComponent } from 'src/app/shared/page-layout/page-form-section.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { WarehousingItem, WarehousingMasterData } from 'src/app/warehousing/warehousing.model';
import { itemSizeLabel } from 'src/app/warehousing/warehousing-item-display';
import { WarehousingService } from 'src/app/warehousing/warehousing.service';
import { WarehousingItemFormComponent } from 'src/app/warehousing/items/warehousing-item-form.component';
import { PosmDeliverySchedule, PosmDeliveryScheduleQuantitySummary, PosmLocationReuseCandidate, PosmProjectStore,
    PosmQuantityCorrectionPreview, PosmDeliveryScheduleAllocation, PosmQuantityCorrectionRequest } from '../../models/posm-execution.models';
import { PosmProjectItem } from '../../models/posm-project-item.model';
import { PosmProject } from '../../models/posm-project.model';
import { PosmService } from '../../posm.service';

@Component({
    selector: 'app-posm-delivery-schedule-form',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, IftaLabelModule,
        InputGroupModule, InputGroupAddonModule, InputNumberModule, SelectModule, TextareaModule, DialogModule,
        ToastModule, LazyLocationPickerDirective, CustomerFormComponent, PartyQuickCreateDialogComponent, PageFormSectionComponent,
        PageHeroComponent, PageShellComponent],
    providers: [MessageService, DialogService, BaseMasterDataService],
    templateUrl: './posm-delivery-schedule-form.component.html',
    styleUrls: ['./posm-delivery-schedule-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmDeliveryScheduleFormComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private posmService = inject(PosmService);
    private crmService = inject(CrmService);
    private customerService = inject(CustomerService);
    private warehousingService = inject(WarehousingService);
    private sessionStorage = inject(SessionStorageService);
    private messageService = inject(MessageService);
    private dialogService = inject(DialogService);
    private masterDataService = inject(BaseMasterDataService);
    private permissionService = inject(PermissionService);

    projectId?: number;
    lineId?: number;
    companyId?: number;
    isNew = signal(true);
    loading = signal(true);
    saving = signal(false);
    project = signal<PosmProject | null>(null);
    schedule = signal<PosmDeliverySchedule>({});
    original = signal<PosmDeliverySchedule | null>(null);
    stores = signal<PosmProjectStore[]>([]);
    items = signal<PosmProjectItem[]>([]);
    masterItems = signal<WarehousingItem[]>([]);
    countries = signal<Country[]>([]);
    showLocationForm = signal(false);
    readonly canCreateLocation = this.permissionService.hasPermission(app_permissions.location.records.create);
    readonly canCreateItem = this.permissionService.hasPermission(app_permissions.warehousing.items.create);
    suppliers = signal<CrmClient[]>([]);
    contacts = signal<Customer[]>([]);
    warehouses = signal<WarehousingMasterData[]>([]);
    conditions = signal<WarehousingMasterData[]>([]);
    quantitySummary = signal<PosmDeliveryScheduleQuantitySummary | null>(null);
    locationReuseCandidates = signal<PosmLocationReuseCandidate[]>([]);
    applyingLocationSuggestionId = signal<number | null>(null);
    selectedContactId = signal<number | null>(null);
    selectedCustomer = signal<Customer>(new Customer());
    showCustomerForm = signal(false);
    selectedCompany = signal<Company>(new Company());
    correctionVisible = false;
    correctionLoading = false;
    correctionPreview?: PosmQuantityCorrectionPreview;
    correctionQuantity?: number;
    correctionReason = '';
    correctionExisting: { allocationId: number; quantity: number; maximum: number; label: string }[] = [];
    correctionAvailable: { candidate: PosmDeliveryScheduleAllocation; quantity: number; label: string }[] = [];
    readonly canCorrectQuantity = this.permissionService.hasPermission(app_permissions.posm.projects.edit)
        && this.permissionService.hasPermission(app_permissions.transport_planning.plans.apply);

    readonly destinationOptions = [
        { label: 'Same Source Warehouse', value: 'SAME_SOURCE_WAREHOUSE' },
        { label: 'Return Warehouse', value: 'RETURN_WAREHOUSE' },
        { label: 'Return Location', value: 'RETURN_LOCATION' }
    ];
    readonly warehouseDispositionOptions = [
        { label: 'Direct Receipt', value: 'DIRECT_RECEIPT' },
        { label: 'Refurbishment', value: 'REFURBISHMENT' },
        { label: 'Destruction', value: 'DESTRUCTION' }
    ];
    readonly locationDispositionOptions = [{ label: 'Refurbishment', value: 'REFURBISHMENT' }];

    itemOptions = computed(() => this.masterItems().filter(item => item.id && item.isActive !== false).flatMap(item => {
        const linked = this.items().filter(row => row.inventoryItem?.id === item.id);
        const label = [item.itemName, item.itemNumber || item.skuNumber, itemSizeLabel(item)].filter(Boolean).join(' | ');
        const options = linked.map(row => ({
            label: [label, row.sourceWarehousingWarehouseName || row.sourceWarehouse?.name,
                `Project Qty ${row.quantity || 0}`].filter(Boolean).join(' | '), value: `p:${row.id}`
        }));
        if (!linked.length || (this.isNew() && this.schedule().inventoryItemId === item.id && !this.schedule().projectItemId)) {
            options.push({ label: `${label} | Add to project`, value: `m:${item.id}` });
        }
        return options;
    }));
    selectedItemValue = computed(() => this.schedule().projectItemId
        ? `p:${this.schedule().projectItemId}` : this.schedule().inventoryItemId
            ? `m:${this.schedule().inventoryItemId}` : undefined);
    supplierOptions = computed(() => this.suppliers().map(supplier => ({
        label: supplier.companyName || supplier.customerNameLocal || supplier.clientNumber || `Supplier #${supplier.id}`,
        value: supplier.id
    })));
    contactOptions = computed(() => this.contacts().map(contact => ({
        label: [contact.name || `Contact #${contact.id}`, contact.phoneNumber, contact.emailAddress]
            .filter(Boolean).join(' | '),
        value: contact.id
    })));
    warehouseOptions = computed(() => this.warehouses()
        .filter(warehouse => `${warehouse.warehouseOwnershipType || 'COMPANY'}`.toUpperCase() !== 'THIRD_PARTY_VIRTUAL')
        .map(warehouse => ({ label: [warehouse.name, warehouse.code].filter(Boolean).join(' | '), value: warehouse.id })));
    sourceWarehouseOptions = computed(() => this.warehouses()
        .filter(warehouse => warehouse.id && warehouse.isActive !== false)
        .map(warehouse => ({ label: [warehouse.name, warehouse.code].filter(Boolean).join(' | '), value: warehouse.id })));
    conditionOptions = computed(() => this.conditions().map(condition => ({
        label: [condition.name, condition.code].filter(Boolean).join(' | '), value: condition.id
    })));
    selectedStore = computed(() => this.stores().find(store => store.id === this.schedule().storeId));
    selectedItem = computed(() => this.items().find(item => item.id === this.schedule().projectItemId));
    supportsRemovalFlow = computed(() => {
        const project = this.project();
        return [...(project?.jobTypes || []), ...(project?.jobTypeCodes || []), project?.jobType]
            .some(type => ['removal', 'collection'].includes(`${type || ''}`.trim().toLowerCase()));
    });
    quantityLocked = computed(() => {
        const row = this.original();
        return !!row && !!(row.installationTaskId || row.removalTaskId || row.transportReleaseId
            || row.removalTransportReleaseId || row.shipmentId || row.removalShipmentId
            || row.deliveryId || row.removalDeliveryId || row.returnOutcomeCompletedAt);
    });

    openQuantityCorrection(): void {
        if (!this.projectId || !this.lineId || !this.companyId || !this.canCorrectQuantity) return;
        this.correctionLoading = true;
        this.posmService.previewQuantityCorrection(this.projectId, this.lineId, this.companyId).subscribe({
            next: preview => {
                for (const route of [preview.installation, preview.removal]) {
                    if (route?.pickupWindowStart) route.pickupWindowStart = new Date(route.pickupWindowStart);
                    if (route?.pickupWindowEnd) route.pickupWindowEnd = new Date(route.pickupWindowEnd);
                    if (route?.deliveryWindowStart) route.deliveryWindowStart = new Date(route.deliveryWindowStart);
                    if (route?.deliveryWindowEnd) route.deliveryWindowEnd = new Date(route.deliveryWindowEnd);
                }
                this.correctionPreview = preview;
                this.correctionQuantity = preview.quantity;
                this.correctionReason = '';
                this.correctionExisting = preview.allocations.map(row => ({ allocationId: row.id!,
                    quantity: row.allocatedQuantity || 0, maximum: row.allocatedQuantity || 0,
                    label: [row.sourceWarehouseName, row.stockUnitCode, row.sourceType].filter(Boolean).join(' | ') }));
                this.correctionAvailable = preview.availableStock.map(candidate => ({ candidate, quantity: 0,
                    label: [candidate.sourceWarehouseName, candidate.stockUnitCode,
                        `Available ${candidate.availableToAllocate || 0}`].filter(Boolean).join(' | ') }));
                this.correctionVisible = true;
                this.correctionLoading = false;
            },
            error: err => {
                this.correctionLoading = false;
                this.warn(err?.error?.message || 'Unable to preview the quantity correction.');
            }
        });
    }

    submitQuantityCorrection(): void {
        const preview = this.correctionPreview;
        if (!preview || !this.projectId || !this.lineId || !this.companyId || this.correctionLoading) return;
        const quantity = this.correctionQuantity;
        if (!quantity || quantity <= 0 || quantity === preview.quantity) return this.warn('Enter a new positive quantity.');
        if (!this.correctionReason.trim() || this.correctionReason.trim().length > 500)
            return this.warn('Enter a cancellation reason of up to 500 characters.');
        const allocations: PosmQuantityCorrectionRequest['allocations'] = [
            ...this.correctionExisting.map(row => ({ allocationId: row.allocationId, quantity: row.quantity })),
            ...this.correctionAvailable.filter(row => row.quantity > 0).map(row => ({
                projectItemSourceId: row.candidate.projectItemSourceId,
                reservationAllocationId: row.candidate.reservationAllocationId,
                quantity: row.quantity
            }))
        ];
        if (Math.abs(allocations.reduce((sum, row) => sum + (row.quantity || 0), 0) - quantity) > 0.0001)
            return this.warn('Replacement stock allocations must total the new quantity.');
        this.correctionLoading = true;
        this.posmService.applyQuantityCorrection(this.projectId, this.lineId, this.companyId, {
            expectedVersion: preview.version, quantity, reason: this.correctionReason.trim(), allocations,
            installation: preview.installation, removal: preview.removal
        }).subscribe({
            next: result => {
                this.correctionLoading = false;
                this.correctionVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Quantity corrected',
                    detail: `${result.cancelledReleaseIds.length} old releases cancelled; ${result.replacementReleaseIds.length} new releases created.` });
                this.load();
            },
            error: err => {
                this.correctionLoading = false;
                this.warn(err?.error?.message || 'Quantity correction was blocked. No changes were saved.');
            }
        });
    }
    projectedQuantity = computed(() => {
        const summary = this.quantitySummary();
        const itemId = this.schedule().projectItemId;
        const quantity = this.schedule().standQty;
        if (!summary || !itemId || quantity == null || quantity <= 0) return null;
        const old = this.original();
        const previous = old?.projectItemId === itemId ? (old.standQty ?? old.projectItem?.quantity ?? 0) : 0;
        return summary.scheduledQuantity - previous + quantity;
    });
    quantityMismatch = computed(() => {
        const projected = this.projectedQuantity();
        const itemQuantity = this.quantitySummary()?.itemQuantity;
        return projected != null && itemQuantity != null && Math.abs(projected - itemQuantity) > 0.00001;
    });
    dispositionOptions = computed(() => this.schedule().returnDestinationType === 'RETURN_LOCATION'
        ? this.locationDispositionOptions : this.warehouseDispositionOptions);
    requiresReturnCondition = computed(() => {
        const row = this.schedule();
        return row.returnDisposition === 'DIRECT_RECEIPT'
            || !!row.returnDisposition && ['SAME_SOURCE_WAREHOUSE', 'RETURN_WAREHOUSE'].includes(row.returnDestinationType || '');
    });
    removalOutcomeCompleted = computed(() => ['RECEIPT_COMPLETED', 'DESTRUCTION_DRAFTED',
        'REFURBISHMENT_REQUESTED', 'COMPLETED'].includes(`${this.original()?.returnOutcomeStatus || ''}`.toUpperCase()));

    ngOnInit(): void {
        this.projectId = Number(this.route.snapshot.paramMap.get('projectId'));
        this.lineId = Number(this.route.snapshot.paramMap.get('lineId')) || undefined;
        this.isNew.set(!this.lineId);
        this.companyId = this.sessionStorage.getCompany()?.id;
        this.selectedCompany.set(this.sessionStorage.getCompany() || new Company());
        if (!this.projectId || !this.companyId) {
            this.cancel();
            return;
        }
        this.load();
    }

    load(): void {
        const projectId = this.projectId!;
        const companyId = this.companyId!;
        this.loading.set(true);
        forkJoin({
            project: this.posmService.getProject(projectId, companyId),
            stores: this.posmService.getProjectStores(projectId, companyId),
            items: this.posmService.getProjectItems(projectId, companyId),
            schedule: this.lineId
                ? this.posmService.getProjectDeliverySchedule(projectId, this.lineId, companyId)
                : of({} as PosmDeliverySchedule)
        }).pipe(finalize(() => this.loading.set(false))).subscribe({
            next: ({ project, stores, items, schedule }) => {
                this.project.set(project);
                this.stores.set(stores || []);
                this.items.set(items || []);
                const row = this.normalizeDates(schedule);
                this.original.set(this.isNew() ? null : row);
                this.schedule.set(this.isNew() ? {
                    storeId: stores?.length === 1 ? stores[0].id : undefined,
                    locationId: stores?.length === 1 ? stores[0].location?.id : undefined,
                    projectItemId: items?.length === 1 ? items[0].id : undefined,
                    inventoryItemId: items?.length === 1 ? items[0].inventoryItem?.id : undefined,
                    locatedWarehouseId: items?.length === 1 ? items[0].sourceWarehousingWarehouseId : undefined,
                    contactNumberOverride: stores?.length === 1 ? this.defaultStoreContact(stores[0]) : undefined
                } : { ...row, locationId: row.locationId || row.store?.location?.id,
                    inventoryItemId: row.inventoryItemId || row.projectItem?.inventoryItem?.id });
                this.syncSelectedContact();
                this.loadQuantitySummary();
                this.loadLocationReuseCandidates();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Delivery Schedule',
                    detail: 'Unable to load the schedule editor.' });
                this.cancel();
            }
        });
        this.crmService.getClients(companyId, ['SUPPLIER', 'WAREHOUSE_SUPPLIER'])
            .subscribe({ next: rows => this.suppliers.set(rows || []), error: () => this.suppliers.set([]) });
        this.customerService.getCustomersByCompany(companyId, true)
            .subscribe({ next: rows => { this.contacts.set(rows || []); this.syncSelectedContact(); }, error: () => this.contacts.set([]) });
        this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' })
            .subscribe({ next: result => this.warehouses.set(result?.data || []), error: () => this.warehouses.set([]) });
        this.warehousingService.getMasterData('ITEM_CONDITION', { activeStatus: 'ACTIVE' })
            .subscribe({ next: result => this.conditions.set(result?.data || []), error: () => this.conditions.set([]) });
        this.warehousingService.getItems()
            .subscribe({ next: result => this.masterItems.set(result?.data || []),
                error: () => this.warn('Unable to load Warehousing items.') });
        this.masterDataService.getCountries().subscribe({ next: rows => this.countries.set(rows || []),
            error: () => this.countries.set([]) });
    }

    updateField<K extends keyof PosmDeliverySchedule>(key: K, value: PosmDeliverySchedule[K]): void {
        this.schedule.update(row => ({ ...row, [key]: value ?? undefined }));
    }

    onStoreChange(locationId?: number): void {
        const store = this.stores().find(row => row.location?.id === locationId);
        this.schedule.update(row => ({ ...row, locationId, storeId: store?.id,
            contactNumberOverride: row.contactNumberOverride || this.defaultStoreContact(store) }));
        this.syncSelectedContact();
    }

    onItemChange(value?: string): void {
        if (value === this.selectedItemValue()) return;
        const [kind, id] = (value || '').split(':');
        const item = kind === 'p' ? this.items().find(row => row.id === Number(id)) : undefined;
        this.schedule.update(row => ({ ...row, projectItemId: item?.id,
            inventoryItemId: item?.inventoryItem?.id || (kind === 'm' ? Number(id) : undefined),
            locatedWarehouseId: this.isNew() ? item?.sourceWarehousingWarehouseId : row.locatedWarehouseId,
            standQty: undefined }));
        this.loadQuantitySummary();
    }

    onSourceWarehouseChange(warehouseId?: number): void {
        const inventoryId = this.schedule().inventoryItemId;
        const matches = this.items().filter(item => item.inventoryItem?.id === inventoryId
            && item.sourceWarehousingWarehouseId === warehouseId);
        this.schedule.update(row => ({ ...row, locatedWarehouseId: warehouseId,
            projectItemId: matches.length === 1 ? matches[0].id : undefined }));
        this.loadQuantitySummary();
    }

    onLocationCreated(result: PartyQuickCreateResult): void {
        const location = result.record as LocationModel;
        this.showLocationForm.set(false);
        if (location.id) this.onStoreChange(location.id);
    }

    createItem(): void {
        const dialog = this.dialogService.open(WarehousingItemFormComponent, {
            header: 'New Warehousing Item', modal: true, width: '96vw',
            breakpoints: { '960px': '98vw', '640px': '100vw' },
            data: { crmClientId: this.project()?.crmClientId, crmAccountId: this.project()?.clientAccountId }
        });
        dialog.onClose.subscribe((created: WarehousingItem | undefined) => {
            if (created?.id) {
                this.masterItems.update(rows => [created, ...rows.filter(row => row.id !== created.id)]);
                this.onItemChange(`m:${created.id}`);
            }
        });
    }

    onContactChange(contactId?: number): void {
        const contact = this.contacts().find(row => row.id === contactId);
        this.selectedContactId.set(contact?.id || null);
        this.updateField('contactNumberOverride', contact?.phoneNumber);
    }

    onDestinationChange(value?: string): void {
        this.schedule.update(row => ({ ...row, returnDestinationType: value,
            returnDisposition: value === 'RETURN_LOCATION' && row.returnDisposition !== 'REFURBISHMENT'
                ? undefined : row.returnDisposition,
            returnConditionId: value === 'RETURN_LOCATION' ? undefined : row.returnConditionId,
            returnWarehouseId: value === 'RETURN_WAREHOUSE' ? row.returnWarehouseId : undefined,
            returnLocationId: value === 'RETURN_LOCATION' ? row.returnLocationId : undefined }));
    }

    onDispositionChange(value?: string): void {
        this.schedule.update(row => ({ ...row, returnDisposition: value,
            returnConditionId: value === 'DIRECT_RECEIPT' || value && row.returnDestinationType !== 'RETURN_LOCATION'
                ? row.returnConditionId : undefined }));
    }

    save(): void {
        if (!this.projectId || !this.companyId || this.saving() || this.loading()) return;
        const row = this.schedule();
        if ((!row.storeId && !row.locationId) || (!row.projectItemId && !row.inventoryItemId))
            return this.warn('Select a target location and warehousing item.');
        if (this.isNew() && !row.locatedWarehouseId) return this.warn('Select a source warehouse.');
        if (!this.quantityLocked() && (row.standQty == null || row.standQty <= 0
            || !Number.isFinite(row.standQty) || Math.abs(Math.round(row.standQty * 10000) - row.standQty * 10000) > 0.000001)) {
            return this.warn('Enter a positive Stand Qty with at most four decimal places.');
        }
        const hasDate = !!(row.installationDate || row.plannedInstallationDate || row.plannedInstallationDateOverride
            || this.supportsRemovalFlow() && (row.removalDate || row.plannedRemovalDate || row.plannedRemovalDateOverride));
        if (!hasDate) return this.warn(this.supportsRemovalFlow()
            ? 'Add an installation date, a removal date, or both.' : 'Add an installation date.');
        if (this.supportsRemovalFlow() && this.hasRemovalInput(row)) {
            if (!row.returnDestinationType) return this.warn('Select a return destination type.');
            if (row.returnDestinationType === 'RETURN_WAREHOUSE' && !row.returnWarehouseId)
                return this.warn('Select a return warehouse.');
            if (row.returnDestinationType === 'RETURN_LOCATION' && !row.returnLocationId)
                return this.warn('Select a return location.');
            if (!row.returnDisposition) return this.warn('Select a return disposition.');
            if (this.requiresReturnCondition() && !row.returnConditionId)
                return this.warn('Select the return item condition.');
            if (row.returnDestinationType === 'RETURN_LOCATION' && row.returnDisposition !== 'REFURBISHMENT')
                return this.warn('Return locations support refurbishment only.');
        }
        if (!this.isNew() && this.removalOutcomeCompleted())
            return this.warn('Completed return outcomes are locked. Create a new schedule for a different return path.');
        const request: PosmDeliverySchedule = {
            id: row.id, storeId: row.storeId, locationId: row.locationId,
            projectItemId: row.projectItemId, inventoryItemId: row.inventoryItemId,
            locatedWarehouseId: this.isNew() ? row.locatedWarehouseId : undefined,
            standQty: this.quantityLocked() ? undefined : row.standQty,
            supplierClientId: row.supplierClientId,
            plannedInstallationDateOverride: row.plannedInstallationDateOverride,
            removalDate: this.supportsRemovalFlow() ? row.removalDate : undefined,
            plannedRemovalDateOverride: this.supportsRemovalFlow() ? row.plannedRemovalDateOverride : undefined,
            returnDestinationType: this.supportsRemovalFlow() ? row.returnDestinationType : undefined,
            returnWarehouseId: this.supportsRemovalFlow() ? row.returnWarehouseId : undefined,
            returnLocationId: this.supportsRemovalFlow() ? row.returnLocationId : undefined,
            returnDisposition: this.supportRemovalValue(row.returnDisposition),
            returnConditionId: this.supportsRemovalFlow() ? row.returnConditionId : undefined,
            contactNumberOverride: row.contactNumberOverride || undefined,
            comment: row.comment || undefined
        };
        this.saving.set(true);
        const action = this.isNew()
            ? this.posmService.createDeliverySchedule(this.projectId, this.companyId, request)
            : this.posmService.updateDeliverySchedule(this.projectId, this.lineId!, this.companyId, request);
        action.pipe(finalize(() => this.saving.set(false))).subscribe({
            next: saved => this.router.navigate(['/posm/executions', this.projectId, 'delivery-schedule', saved.id],
                { replaceUrl: true }),
            error: error => this.messageService.add({ severity: 'error', summary: 'Delivery Schedule',
                detail: error?.error?.message || (typeof error?.error === 'string' ? error.error : 'Unable to save delivery schedule.') })
        });
    }

    cancel(): void {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        if (returnUrl?.startsWith('/posm/executions/delivery-schedules/')
            && returnUrl.endsWith(`/${this.lineId}`)) {
            this.router.navigateByUrl(returnUrl);
        } else if (this.lineId && this.projectId) {
            this.router.navigate(['/posm/executions', this.projectId, 'delivery-schedule', this.lineId]);
        } else if (this.projectId) {
            this.router.navigate(['/posm/executions', this.projectId], { queryParams: { tab: 'delivery-schedule' } });
        } else {
            this.router.navigate(['/posm/executions']);
        }
    }

    createContact(): void {
        const customer = new Customer();
        customer.phoneNumber = this.schedule().contactNumberOverride || this.defaultStoreContact(this.selectedStore()) || '';
        customer.company = this.selectedCompany();
        this.selectedCustomer.set(customer);
        this.showCustomerForm.set(true);
    }

    onCustomerFormClosed(result?: Customer | boolean): void {
        this.showCustomerForm.set(false);
        if (result && typeof result === 'object' && result.id) {
            this.contacts.update(rows => [result, ...rows.filter(row => row.id !== result.id)]);
            this.onContactChange(result.id);
        }
    }

    createSupplier(): void {
        this.router.navigate(['/crm/clients/new'], {
            queryParams: { category: 'SUPPLIER', returnUrl: this.router.url }
        });
    }

    applySuggestedLocation(locationId: number): void {
        const store = this.selectedStore();
        if (!this.projectId || !this.companyId || !store?.id) return this.warn('Select a target store first.');
        this.applyingLocationSuggestionId.set(locationId);
        this.posmService.updateProjectStore(this.projectId, store.id, this.companyId,
            { ...store, location: { id: locationId } as LocationModel })
            .pipe(finalize(() => this.applyingLocationSuggestionId.set(null)))
            .subscribe({
                next: () => {
                    this.posmService.getProjectStores(this.projectId!, this.companyId!).subscribe({
                        next: stores => this.stores.set(stores || []),
                        error: () => {}
                    });
                    this.locationReuseCandidates.set([]);
                },
                error: error => this.messageService.add({ severity: 'error', summary: 'Location Reuse',
                    detail: error?.error?.message || 'Unable to reuse the selected location.' })
            });
    }

    private loadQuantitySummary(): void {
        const itemId = this.schedule().projectItemId;
        if (!itemId || !this.projectId || !this.companyId) {
            this.quantitySummary.set(null);
            return;
        }
        this.quantitySummary.set(null);
        this.posmService.getDeliveryScheduleQuantitySummary(this.projectId, itemId, this.companyId)
            .subscribe({ next: summary => {
                if (this.schedule().projectItemId === itemId) this.quantitySummary.set(summary);
            }, error: () => this.quantitySummary.set(null) });
    }

    private loadLocationReuseCandidates(): void {
        const row = this.schedule();
        if (!this.lineId || !this.projectId || !this.companyId || !(row.mappingWarnings || [])
            .some(warning => `${warning || ''}`.toLowerCase().includes('store location'))) {
            this.locationReuseCandidates.set([]);
            return;
        }
        this.posmService.getDeliveryScheduleLocationReuseCandidates(this.projectId, this.lineId, this.companyId, 0, 5)
            .subscribe({ next: result => this.locationReuseCandidates.set(result?.items || []),
                error: () => this.locationReuseCandidates.set([]) });
    }

    private normalizeDates(row: PosmDeliverySchedule): PosmDeliverySchedule {
        const date = (value?: Date | string): Date | undefined => value ? new Date(value) : undefined;
        return { ...row,
            installationDate: date(row.installationDate), plannedInstallationDate: date(row.plannedInstallationDate),
            plannedInstallationDateOverride: date(row.plannedInstallationDateOverride),
            removalDate: date(row.removalDate), plannedRemovalDate: date(row.plannedRemovalDate),
            plannedRemovalDateOverride: date(row.plannedRemovalDateOverride) };
    }

    private syncSelectedContact(): void {
        const phone = `${this.schedule().contactNumberOverride || this.schedule().contactNumber
            || this.defaultStoreContact(this.selectedStore()) || ''}`.replace(/\s+/g, '');
        this.selectedContactId.set(this.contacts().find(contact => `${contact.phoneNumber || ''}`.replace(/\s+/g, '') === phone)?.id || null);
    }

    private defaultStoreContact(store?: PosmProjectStore): string | undefined {
        return store?.contactPersonPhone || store?.location?.telephoneNumber || undefined;
    }

    private hasRemovalInput(row: PosmDeliverySchedule): boolean {
        return !!(row.removalDate || row.plannedRemovalDate || row.plannedRemovalDateOverride
            || row.returnDestinationType || row.returnWarehouseId || row.returnLocationId || row.returnDisposition
            || row.removalTaskId || row.removalTransportReleaseId || row.removalDeliveryId);
    }

    private supportRemovalValue(value?: string): string | undefined {
        return this.supportsRemovalFlow() ? value : undefined;
    }

    private warn(detail: string): void {
        this.messageService.add({ severity: 'warn', summary: 'Delivery Schedule', detail });
    }
}
